import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faChevronLeft,
  faTimes,
  faPhone,
  faEnvelope,
  faTicket,
  faKey,
  faUser,
  faStickyNote,
  faShare,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { doc, updateDoc, Timestamp, collection, addDoc, getDocs, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './GuestList.module.css';

const GuestModal = ({ isOpen, onClose, guest, onSave, tourMembers, isProcessing }) => {
  const [guestData, setGuestData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    ticketType: '',
    accessLevel: '',
    requestedBy: '',
    notes: ''
  });

  useEffect(() => {
    if (guest) {
      setGuestData(guest);
    } else {
      setGuestData({
        fullName: '',
        phoneNumber: '',
        email: '',
        ticketType: '',
        accessLevel: '',
        requestedBy: '',
        notes: ''
      });
    }
  }, [guest]);

  const accessLevels = ['AAA', 'AA', 'Media', 'Crew'];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(guestData);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{guest ? 'Edit Guest' : 'Add Guest'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label htmlFor="fullName">Full Name *</label>
            <input
              id="fullName"
              type="text"
              value={guestData.fullName}
              onChange={(e) => setGuestData({ ...guestData, fullName: e.target.value })}
              required
              className={styles.input}
              placeholder="Enter full name"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                id="phoneNumber"
                type="tel"
                value={guestData.phoneNumber}
                onChange={(e) => setGuestData({ ...guestData, phoneNumber: e.target.value })}
                className={styles.input}
                placeholder="Enter phone number"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={guestData.email}
                onChange={(e) => setGuestData({ ...guestData, email: e.target.value })}
                className={styles.input}
                placeholder="Enter email"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="ticketType">Ticket Type</label>
              <input
                id="ticketType"
                type="text"
                value={guestData.ticketType}
                onChange={(e) => setGuestData({ ...guestData, ticketType: e.target.value })}
                className={styles.input}
                placeholder="Enter ticket type"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="accessLevel">Access Level</label>
              <select
                id="accessLevel"
                value={guestData.accessLevel}
                onChange={(e) => setGuestData({ ...guestData, accessLevel: e.target.value })}
                className={styles.select}
              >
                <option value="">Select Access Level</option>
                {accessLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="requestedBy">Requested By</label>
            <select
              id="requestedBy"
              value={guestData.requestedBy}
              onChange={(e) => setGuestData({ ...guestData, requestedBy: e.target.value })}
              className={styles.select}
            >
              <option value="">Select Person</option>
              {tourMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.fullName} ({member.userType})
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={guestData.notes}
              onChange={(e) => setGuestData({ ...guestData, notes: e.target.value })}
              className={styles.textarea}
              rows={4}
              placeholder="Enter notes"
            />
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Simple PDF Modal Component
const PdfModal = ({ isOpen, onClose, pdfHtml }) => {
  if (!isOpen) return null;

  const handlePrint = () => {
    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(pdfHtml);
    printFrame.contentDocument.close();
    
    // Use setTimeout to avoid blocking
    setTimeout(() => {
      printFrame.contentWindow.print();
      // Remove iframe after a delay
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 100);
    }, 100);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.pdfModalContent}>
        <div className={styles.pdfModalHeader}>
          <h2>Guest List Preview</h2>
          <div className={styles.pdfModalActions}>
            <button className={styles.printButton} onClick={handlePrint}>
              <FontAwesomeIcon icon={faShare} />
              Print
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        <div className={styles.pdfModalBody}>
          <iframe
            srcDoc={pdfHtml}
            className={styles.pdfIframe}
            title="Guest List Preview"
          />
        </div>
      </div>
    </div>
  );
};

const GuestList = () => {
  const navigate = useNavigate();
  const { eventId, dateId } = useParams();
  const location = useLocation();
  const { selectedEvent, getSelectedDate, updateDate } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  const [guestList, setGuestList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfHtml, setPdfHtml] = useState('');

  // Determine event type from URL or selected event
  const eventType = selectedEvent?.type || (location.pathname.includes('/tour/') ? 'tour' : 'show');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadGuestList();
  }, [eventId, dateId, selectedEvent]);

  const loadGuestList = async () => {
    if (!eventId || !dateId) return;
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Query the guestlist subcollection
      const guestlistQuery = query(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'guestlist'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(guestlistQuery);
      const guests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setGuestList(guests);
    } catch (error) {
      console.error('Error loading guest list:', error);
    }
  };

  const getTourMembers = () => {
    if (!selectedEvent) return [];
    
    const members = [];
    
    // Add manager
    if (selectedEvent.createdBy) {
      members.push({
        userId: selectedEvent.createdBy.userId,
        fullName: selectedEvent.createdBy.fullName,
        userType: 'Manager'
      });
    }

    // Add artists from assignedUsers subcollection
    // This is a simplified approach. In a real implementation, 
    // you would query the assignedUsers subcollection
    if (selectedEvent.artistDetails) {
      selectedEvent.artistDetails.forEach(artist => {
        members.push({
          userId: artist.userId,
          fullName: artist.fullName || artist.name,
          userType: 'Artist'
        });
      });
    }

    // Add crew
    if (selectedEvent.crewDetails) {
      selectedEvent.crewDetails.forEach(crew => {
        members.push({
          userId: crew.userId,
          fullName: crew.fullName || crew.name,
          userType: 'Crew'
        });
      });
    }

    return members;
  };

  const handleAddGuest = () => {
    setEditingGuest(null);
    setIsModalOpen(true);
  };

  const handleEditGuest = (guest) => {
    setEditingGuest(guest);
    setIsModalOpen(true);
  };

  const handleBack = () => {
    navigate(`/${eventType}/${selectedEvent.id}/${dateId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSaveGuest = async (guestData) => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      if (editingGuest) {
        // Update existing guest
        const guestRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'guestlist', editingGuest.id);
        await updateDoc(guestRef, {
          ...guestData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add new guest
        await addDoc(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'guestlist'), 
          {
            ...guestData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }
        );
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the guest list
      await loadGuestList();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      setIsModalOpen(false);

    } catch (error) {
      console.error('Error saving guest:', error);
      alert('Failed to save guest. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteGuest = async (guestToDelete) => {
    if (!window.confirm('Are you sure you want to delete this guest?')) return;

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the guest document
      await deleteDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId, 'guestlist', guestToDelete.id)
      );

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the guest list
      await loadGuestList();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);

    } catch (error) {
      console.error('Error deleting guest:', error);
      alert('Failed to delete guest. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const html = generateGuestListHtml();
      setPdfHtml(html);
      setShowPdfModal(true);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const generateGuestListHtml = () => {
    const eventDate = getSelectedDate();
    const formattedDate = eventDate?.date.split('-').reverse().join('-');
    const tourMembers = getTourMembers();

    const rows = guestList.map((guest) => {
      const requestedByUser = tourMembers.find((user) => user.userId === guest.requestedBy);
      const requestedByName = requestedByUser ? requestedByUser.fullName : '';

      return `
        <tr>
          <td>${guest.id.slice(0, 6)}</td>
          <td>${guest.fullName}</td>
          <td>${guest.phoneNumber || ''}</td>
          <td>${guest.email || ''}</td>
          <td>${guest.ticketType || ''}</td>
          <td>${guest.accessLevel || ''}</td>
          <td>${requestedByName}</td>
          <td style="width: 200px; word-wrap: break-word;">${guest.notes || ''}</td>
        </tr>
      `;
    });

    return `
      <html>
        <head>
          <style>
            @page {
              size: A4 landscape;
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            h1, h2, h3 {
              text-align: center;
              margin: 10px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <h1>${selectedEvent.tourName || selectedEvent.showName}</h1>
          <h2>${selectedEvent.actName || ''}</h2>
          <h3>${formattedDate}</h3>
          <h2>Guest List</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Full Name</th>
                <th>Phone Number</th>
                <th>Email</th>
                <th>Ticket Type</th>
                <th>Access Level</th>
                <th>Requested By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${rows.join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
  };

  const getMemberName = (userId) => {
    const member = getTourMembers().find(member => member.userId === userId);
    return member ? member.fullName : 'Unknown User';
  };

  if (!selectedEvent || !dateId) {
    return (
      <div className={styles.layout}>
        <main className={styles.main}>
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faUser} size="2x" />
            <p>Please select an event and date to view guest list</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        <main className={styles.content}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
              Back to Schedule
            </button>
            <h1>{selectedEvent.tourName || selectedEvent.showName}</h1>
            <h2>{formatDate(getSelectedDate()?.date)}</h2>
          </div>

          <div className={styles.guestSection}>
            <div className={styles.sectionHeader}>
              <button className={styles.createButton} onClick={handleAddGuest}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Guest</span>
              </button>
              <button className={styles.exportButton} onClick={handleExportPDF}>
                <FontAwesomeIcon icon={faFilePdf} />
                <span>Export PDF</span>
              </button>
            </div>

            <div className={styles.guestList}>
              {guestList.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No guests available</p>
                </div>
              ) : (
                guestList.map(guest => (
                  <div key={guest.id} className={styles.guestCard}>
                    <div className={styles.guestHeader}>
                      <h3>{guest.fullName}</h3>
                      <div className={styles.guestActions}>
                        <button
                          onClick={() => handleEditGuest(guest)}
                          className={styles.actionButton}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteGuest(guest)}
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.guestDetails}>
                      <div className={styles.detailRow}>
                        <div className={styles.detailItem}>
                          <FontAwesomeIcon icon={faPhone} className={styles.detailIcon} />
                          <span>{guest.phoneNumber || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                          <FontAwesomeIcon icon={faEnvelope} className={styles.detailIcon} />
                          <span>{guest.email || 'N/A'}</span>
                        </div>
                      </div>
                      <div className={styles.detailRow}>
                        <div className={styles.detailItem}>
                          <FontAwesomeIcon icon={faTicket} className={styles.detailIcon} />
                          <span>{guest.ticketType || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                          <FontAwesomeIcon icon={faKey} className={styles.detailIcon} />
                          <span>{guest.accessLevel || 'N/A'}</span>
                        </div>
                      </div>
                      <div className={styles.detailRow}>
                        <div className={styles.detailItem}>
                          <FontAwesomeIcon icon={faUser} className={styles.detailIcon} />
                          <span>Requested by: {getMemberName(guest.requestedBy) || 'N/A'}</span>
                        </div>
                      </div>
                      {guest.notes && (
                        <div className={styles.notesRow}>
                          <FontAwesomeIcon icon={faStickyNote} className={styles.detailIcon} />
                          <span className={styles.notes}>{guest.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {isModalOpen && (
            <GuestModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              guest={editingGuest}
              onSave={handleSaveGuest}
              tourMembers={getTourMembers()}
              isProcessing={isProcessing}
            />
          )}

          {showPdfModal && (
            <PdfModal
              isOpen={showPdfModal}
              onClose={() => setShowPdfModal(false)}
              pdfHtml={pdfHtml}
            />
          )}

          {isProcessing && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Processing...</p>
            </div>
          )}
        </main>
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={[]}
            onEventSelect={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default GuestList;